/**
 * @fileoverview Error-storm suppression for the server transport (Q-UMB-13).
 *
 * One broken render loop, a failing poll or a listener that throws on every scroll produces the
 * SAME entry hundreds of times a second. Shipped as-is, that storm spends the page's share of the
 * ingest's per-IP limit (120 batches/min, shared by every tab behind one NAT) on one fact, and the
 * 429s that follow block every OTHER error the page has. So an entry is keyed by `level + message`
 * and admitted at most `maxPerWindow` times per `windowMs`; the rest are COUNTED, never lost as a
 * number — the next admitted entry for that key carries `suppressedRepeats`, so an error population
 * is still counted whole (the standard's rule: WARNING+ is never sampled).
 *
 * The key table is bounded (`maxKeys`, oldest evicted) so a storm of DISTINCT messages cannot grow
 * memory either. The clock is injected.
 *
 * @module transports/repeatSuppressor
 */

/**
 * @param {Object} [options]
 * @param {number} [options.windowMs=60000] - the window a key's admissions are counted over
 * @param {number} [options.maxPerWindow=5] - admissions per key per window
 * @param {number} [options.maxKeys=200] - bound on tracked keys
 * @param {() => number} [options.now=Date.now] - clock
 * @returns {{admit: (key: string) => {admit: boolean, suppressedRepeats: number}, size: () => number}}
 */
export function createRepeatSuppressor({
  windowMs = 60000,
  maxPerWindow = 5,
  maxKeys = 200,
  now = Date.now,
} = {}) {
  /** key → { windowStart, admitted, suppressed } — insertion order is eviction order. */
  const keys = new Map();

  function admit(key) {
    const at = now();
    let slot = keys.get(key);
    if (slot && at - slot.windowStart >= windowMs) {
      // A new window for this key: the count it suppressed rides on the entry that opens it.
      slot = { windowStart: at, admitted: 0, suppressed: slot.suppressed };
      keys.delete(key);
      keys.set(key, slot);
    }
    if (!slot) {
      if (keys.size >= maxKeys) keys.delete(keys.keys().next().value);
      slot = { windowStart: at, admitted: 0, suppressed: 0 };
      keys.set(key, slot);
    }
    if (slot.admitted >= maxPerWindow) {
      slot.suppressed += 1;
      return { admit: false, suppressedRepeats: 0 };
    }
    slot.admitted += 1;
    const suppressedRepeats = slot.suppressed;
    slot.suppressed = 0;
    return { admit: true, suppressedRepeats };
  }

  return { admit, size: () => keys.size };
}
